import { Cell, Row } from "@tanstack/react-table";

import { GeoJSONSource } from "maplibre-gl";
import { FeatureCollection, GeoJsonProperties } from "geojson";

import NotificationContent from "@/components/notification/notificationContent";
import {
  ExtendedColumnDef,
  SpatialAnalysisRow
} from "@/components/spatialAnalysis/spatialAnalysisTable/columns";
import { ERROR_MESSAGE, MESSAGE } from "@/constants/message";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import {
  ATTRIBUTE_ID,
  DataSchemaType,
  DatasetAttributeSchema,
  DatasetSchemaLabel
} from "@/types/dataset";

import useToast, { EToastType } from "../ui/useToast";

export type PreviewData = {
  value: string | null;
  type: "grid" | "wkt";
};

interface IUsePreviewGeom {
  handleClose: () => void;
  selectedDatasetId: string | null | undefined;
  attributes?: DatasetAttributeSchema[];
}

export const usePreviewGeom = ({
  handleClose,
  selectedDatasetId,
  attributes
}: IUsePreviewGeom) => {
  const { handleHighlightDatasetFeatureByWkt } = useDataset();
  const { showToast } = useToast();
  const { map } = useMap();

  const handlePreviewClick = async (
    value: string | null,
    type: "grid" | "wkt"
  ) => {
    if (!value || !selectedDatasetId) {
      showToast({
        type: EToastType.ERROR,
        message: (
          <NotificationContent
            title={ERROR_MESSAGE.SOMETHING_WENT_WRONG}
            description={MESSAGE.MAP_VIEW_ACTION_COLUMN_FAILED}
          />
        ),
        autoClose: 2000
      });
      return;
    }

    let highlightValue: string | GeoJsonProperties = value;

    if (type === "grid") {
      const source = map?.getSource<GeoJSONSource>(selectedDatasetId);
      if (!source) return;
      const gridLikeAttr = attributes?.find(
        (attr) =>
          attr.is_reference_geo ||
          attr.data_label === DatasetSchemaLabel.GEOHASH ||
          attr.data_label === DatasetSchemaLabel.H3 ||
          attr.attribute_name === ATTRIBUTE_ID.catchment_id ||
          attr.code === ATTRIBUTE_ID.catchment_id
      );
      const h3OrGridName = gridLikeAttr?.attribute_name || "";

      const featureCollection = (await source.getData()) as FeatureCollection;

      const feature = featureCollection.features.find((f) => {
        const properties = f.properties ?? {};
        return (
          properties[h3OrGridName] === value ||
          properties["Grid ID"] === value ||
          properties["H3 ID"] === value ||
          properties[ATTRIBUTE_ID.catchment_id] === value ||
          // Fallback: the grid id (H3/geohash) is unique, so match it against any
          // property when the source feature stores it under an unexpected key
          // (or when no attribute metadata is available to resolve the column).
          Object.values(properties).includes(value)
        );
      });

      if (!feature) return;
      highlightValue = feature;
    }

    handleHighlightDatasetFeatureByWkt({
      datasetId: selectedDatasetId,
      value: highlightValue,
      type
    });

    handleClose();
  };

  // `cell.column.columnDef` is typed as TanStack's base `ColumnDef`, which does
  // not expose the dataset-specific fields we attach (`type`, `data_label`,
  // `is_reference_geo`). This helper localises that single assertion so the
  // callers can read those fields in a type-safe way.
  const getColumnDef = (cell: Cell<SpatialAnalysisRow, unknown>) =>
    cell.column.columnDef as ExtendedColumnDef<SpatialAnalysisRow>;

  const getPreviewDataFromRow = (row: Row<SpatialAnalysisRow>): PreviewData => {
    const { grid_id, h3_id } = row.original;

    // The reference-geo column (`is_reference_geo === true`) is the geometry
    // the dataset is actually rendered with on the map, so the row preview must
    // read from that column regardless of how many geometry columns exist.
    const referenceGeoCell = row
      .getAllCells()
      .find((cell) => getColumnDef(cell).is_reference_geo === true);

    const referenceDataType = referenceGeoCell
      ? getColumnDef(referenceGeoCell).type
      : undefined;

    const rawReferenceValue = referenceGeoCell?.getValue();
    const referenceValue =
      typeof rawReferenceValue === "string" ? rawReferenceValue : undefined;

    // A reference geometry stored as raw WKT (point/polygon/line) is highlighted
    // directly. Every other reference geometry (H3, geohash, adm area, grid id)
    // is a non-geometry id column and is highlighted via a feature lookup, so it
    // must be treated as a grid.
    if (referenceDataType === DataSchemaType.GEOMETRY) {
      return {
        value: referenceValue ?? null,
        type: "wkt"
      };
    }

    const gridValue = referenceValue || grid_id || h3_id;

    return {
      value: gridValue ? String(gridValue) : null,
      type: "grid"
    };
  };

  return { handlePreviewClick, getPreviewDataFromRow };
};
